import React from 'react';
import { DynamicField, DynamicFieldTypes } from 'src/components/dynamic-widget';
import DynamicFormWidget from 'src/components/dynamic-widget/DynamicFormWidget';
import { DataService } from 'src/services/data-service';
import GenericDialog from 'src/components/GenericDialog';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { IContactDetails, IUpdateContactModel } from '.';

interface IUpdateContactProps {
  onSuccess: () => void;
  onClose: () => void;
  contact: IContactDetails;
}

const UpdateContact: React.FC<IUpdateContactProps> = ({ onSuccess, onClose, contact }) => {
  const dispatch = useAppDispatch();

  const formConfig = React.useMemo((): Record<string, DynamicFieldTypes> => ({
    fullName: {
      type: DynamicField.TEXT,
      required: true,
      name: 'fullName',
      title: 'Full Name',
      value: contact?.fullName || '',
      disabled: false
    },
    mobileNumber: {
      type: DynamicField.TEXT,
      required: true,
      name: 'mobileNumber',
      title: 'Mobile Number',
      value: contact?.mobileNumber || '',
      disabled: false
    },
    email: {
      type: DynamicField.TEXT,
      required: true,
      name: 'email',
      title: 'Email',
      value: contact?.email || '',
      disabled: false
    },
    dateOfBirth: {
      type: DynamicField.DATE,
      required: true,
      name: 'dateOfBirth',
      title: 'Date of Birth',
      value: contact?.dateOfBirth || '',
      disabled: false
    },
    passportNumber: {
      type: DynamicField.TEXT,
      required: false,
      name: 'passportNumber',
      title: 'Passport Number',
      value: contact?.passportNumber || '',
      disabled: false
    },
    passportExpiryDate: {
      type: DynamicField.DATE,
      required: false,
      name: 'passportExpiryDate',
      title: 'Passport Expiry Date',
      value: contact?.passportExpiryDate || '',
      disabled: false
    },
    nationalIdNumber: {
      type: DynamicField.TEXT,
      required: false,
      name: 'nationalIdNumber',
      title: 'National ID Number',
      value: contact?.nationalIdNumber || '',
      disabled: false
    },
    addressInLebanon: {
      type: DynamicField.TEXTAREA,
      required: false,
      name: 'addressInLebanon',
      title: 'Address in Lebanon',
      value: contact?.addressInLebanon || '',
      disabled: false
    },
    addressInKinshasa: {
      type: DynamicField.TEXTAREA,
      required: false,
      name: 'addressInKinshasa',
      title: 'Address in Kinshasa',
      value: contact?.addressInKinshasa || '',
      disabled: false
    }
  }), [contact]);

  const toUtcMidnight = (s?: string) =>
  s ? new Date(`${s}T00:00:00Z`).toISOString() : null;

  const submit = React.useCallback(async (data: Record<string, any>) => {
    const payload: IUpdateContactModel = {
      id: contact.id,
      fullName: data.fullName,
      mobileNumber: data.mobileNumber,
      email: data.email,
      dateOfBirth: toUtcMidnight(data.dateOfBirth),    
      passportNumber: data.passportNumber || '',
      passportExpiryDate: toUtcMidnight(data.passportExpiryDate),
      nationalIdNumber: data.nationalIdNumber || '',
      addressInLebanon: data.addressInLebanon || '',
      addressInKinshasa: data.addressInKinshasa || ''
    };

    const response = await DataService.post(
      'api/Contact/update',
      payload,
      undefined
    );

    if (response.ok) {
      onSuccess();
      return true;
    } else {
      const result: { message: string } = await response.json();
      dispatch(showAlert({
        message: result.message || 'An error occurred while updating contact!',
        type: 'error'
      }));
      return false;
    }
  }, [dispatch, onSuccess, contact]);

  return (
    <GenericDialog
      open={true}
      title="Edit Contact"
      onClose={onClose}
    >
      <DynamicFormWidget
        title=""
        fields={formConfig}
        onSubmit={submit}
      />
    </GenericDialog>
  );
};

export default UpdateContact;
